import React, { FC, useState } from 'react';
import {
  Box,
  Divider,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useGetWebhookListQuery, Webhook, WebhookTypes } from 'api';
import { getAppImage } from 'helpers';

import Menu from './Integrations/Menu';
import WorkvivoModal from './Integrations/WorkVivoModal';
import GenericWebhookModal from './Integrations/GenericWebhookModal';

enum IntegrationType {
  Generic = 'generic',
  Workvivo = 'workVivo',
  Placeholder = 'placeholder',
}

const Integrations: FC = () => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [isOpenWorkVivo, setIsOpenWorkVivo] = useState(false);
  const [isOpenGeneric, setIsOpenGeneric] = useState(false);
  const [value, setValue] = useState<IntegrationType>(IntegrationType.Placeholder);

  const { data } = useGetWebhookListQuery();

  function renderIntegrations() {
    const webhooks = data?.getWebhookList?.webhooks || [];

    return webhooks.map((webhook: Webhook) => {
      const isWorkvivo = webhook.webhook_type === WebhookTypes.Workvivo;

      return (
        <Grid container alignItems="center" key={webhook.webhook_id}>
          <Grid xs={3} />
          <Grid xs={7}>
            <Divider />
            <Box marginY={3}>
              <Grid container justifyContent="space-between">
                <Grid xs={11}>
                  {isWorkvivo ? (
                    <Grid container alignItems="center" spacing={2}>
                      <Grid>
                        <img alt="workvivo_icon" src={getAppImage('wv_Icon.png', 30).src} />
                      </Grid>
                      <Grid>
                        <Typography variant="subtitle2">{t('admin:workvivo')}</Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="subtitle2">{webhook.webhook_name}</Typography>
                  )}
                </Grid>
                <Grid>
                  <Menu {...webhook} />
                </Grid>

                <Box marginY={3} />

                <Grid xs={12} container>
                  <Grid xs={5}>
                    <Typography variant="caption" color="textSecondary">
                      {isWorkvivo ? t('admin:workvivoId') : t('common:url')}
                    </Typography>
                    <Typography variant="subtitle2" color="textPrimary">
                      {isWorkvivo ? webhook.workvivo_org_id : webhook.webhook_id}
                    </Typography>
                  </Grid>

                  <Grid xs={5}>
                    <Typography variant="caption" color="textSecondary">
                      {isWorkvivo ? t('common:authorization') : t('admin:secret')}
                    </Typography>
                    <Typography variant="subtitle2" color="textPrimary">
                      {'***********'}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
            <Divider />
          </Grid>
        </Grid>
      );
    });
  }

  function onClose() {
    setIsOpen(false);
    setValue(IntegrationType.Placeholder);
  }

  function onChangeDropdown(event: SelectChangeEvent<IntegrationType>) {
    switch (event.target.value) {
      case IntegrationType.Workvivo:
        setIsOpenWorkVivo(true);
        break;
      case IntegrationType.Generic:
        setIsOpenGeneric(true);
        break;
    }

    setValue(IntegrationType.Placeholder);
  }

  return (
    <Box minHeight="20vh">
      <Grid container alignItems="center">
        <Grid xs={3}>
          <Typography variant="subtitle2">{t('admin:integrations')}</Typography>
        </Grid>

        <Grid xs={3}>
          <Select
            variant="standard"
            fullWidth
            value={value}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={onClose}
            onChange={onChangeDropdown}
          >
            <MenuItem value={IntegrationType.Placeholder} disabled>
              {t('admin:selectIntegration')}
            </MenuItem>
            <MenuItem value={IntegrationType.Workvivo}>{t('admin:workvivo')}</MenuItem>
            <MenuItem value={IntegrationType.Generic}>{t('admin:genericWebhook')}</MenuItem>
          </Select>
        </Grid>
      </Grid>

      <WorkvivoModal isOpen={isOpenWorkVivo} setIsOpen={setIsOpenWorkVivo} />
      <GenericWebhookModal isOpen={isOpenGeneric} setIsOpen={setIsOpenGeneric} />

      <Box marginTop={6}>{renderIntegrations()}</Box>
    </Box>
  );
};

export default Integrations;
